/**
 * One member, opened from the members table: who they are, their role (the
 * admin switch), their allowance with its usage meter, and — behind a
 * confirmation — removing them from the organization.
 *
 * @module components/settings/MemberSheet
 */
import { SlidersHorizontalIcon, UserMinusIcon } from "lucide-react";
import { useState, type ReactNode } from "react";

import type { ShowableModelSpend } from "../../state/modelSpend";
import type { OrgMemberAction, OrgTeamMember } from "../../state/orgProfile";
import {
  AlertDialog,
  AlertDialogClose,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogPopup,
  AlertDialogTitle,
} from "../ui/alert-dialog";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Sheet, SheetDescription, SheetHeader, SheetPanel, SheetPopup, SheetTitle } from "../ui/sheet";
import { Switch } from "../ui/switch";
import { MemberAllowanceDialog } from "./MemberAllowanceDialog";
import { MemberAvatar } from "./MemberAvatar";
import { MemberUsagePanel } from "./MemberUsage";
import {
  isAdminMember,
  memberDisplayName,
  memberJoinedDate,
  memberPositionLabel,
} from "./orgMembers.logic";

export interface MemberSheetProps {
  /** The member shown; null closes the sheet. */
  readonly member: OrgTeamMember | null;
  readonly onClose: () => void;
  readonly viewerUserId: string | null;
  /** Organization admin: may change roles, remove people and switch on-demand usage. */
  readonly canManage: boolean;
  /** Amoreg operator: may set allowances. */
  readonly isOperator: boolean;
  readonly standardGbp: number | null;
  readonly overageMultiplier: number | null;
  /** This member's spend this month, when the page has it. */
  readonly spendFor: (member: OrgTeamMember) => ShowableModelSpend | null;
  readonly applyMemberAction: (userId: string, action: OrgMemberAction) => Promise<string | null>;
  readonly onToggleOnDemand: (userId: string, enabled: boolean) => Promise<string | null>;
  readonly onSetAllowance: (userId: string, allowanceGbp: number | null) => Promise<string | null>;
}

export function MemberSheet(props: MemberSheetProps) {
  const { member, onClose } = props;
  // Keep drawing the last member while the sheet animates closed.
  const [shown, setShown] = useState<OrgTeamMember | null>(member);
  if (member !== null && member !== shown) setShown(member);
  return (
    <Sheet
      open={member !== null}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <SheetPopup side="right" variant="inset" className="max-w-[420px]">
        {shown ? <MemberSheetBody key={shown.id} {...props} member={shown} /> : null}
      </SheetPopup>
    </Sheet>
  );
}

function MemberSheetBody({
  member,
  onClose,
  viewerUserId,
  canManage,
  isOperator,
  standardGbp,
  overageMultiplier,
  spendFor,
  applyMemberAction,
  onToggleOnDemand,
  onSetAllowance,
}: MemberSheetProps & { readonly member: OrgTeamMember }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [limitsOpen, setLimitsOpen] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState(false);
  const name = memberDisplayName(member);
  const position = memberPositionLabel(member);
  const joined = memberJoinedDate(member.joinedAt);
  const isAdmin = isAdminMember(member);
  const isViewer = member.id === viewerUserId;

  const setRole = (admin: boolean) => {
    setBusy(true);
    setError(null);
    void applyMemberAction(member.id, { kind: "role", role: admin ? "admin" : "member" }).then(
      (failure) => {
        setBusy(false);
        setError(failure);
      },
    );
  };

  const remove = () => {
    setBusy(true);
    setError(null);
    void applyMemberAction(member.id, { kind: "remove" }).then((failure) => {
      setBusy(false);
      setConfirmRemove(false);
      if (failure) setError(failure);
      else onClose();
    });
  };

  return (
    <>
      <SheetHeader className="gap-4 pb-5">
        <MemberAvatar member={member} className="size-14 [&_[data-slot=avatar-fallback]]:text-base" />
        <div className="min-w-0 space-y-1.5">
          <div className="flex flex-wrap items-center gap-2">
            <SheetTitle className="truncate text-lg">{name}</SheetTitle>
            <Badge variant={isAdmin ? "info" : "outline"}>{isAdmin ? "Admin" : "Member"}</Badge>
            {isViewer ? <Badge variant="secondary">You</Badge> : null}
          </div>
          <SheetDescription className="truncate">
            {member.name ? member.email : (position ?? "No name on the account yet")}
          </SheetDescription>
        </div>
      </SheetHeader>
      <SheetPanel className="space-y-6">
        <SheetSection title="Details">
          <dl className="grid grid-cols-2 gap-x-3 gap-y-3 text-sm">
            <Detail label="Title" value={position ?? "—"} />
            <Detail label="Joined" value={joined ?? "—"} />
          </dl>
        </SheetSection>

        <SheetSection title="Role">
          <label className="flex items-start justify-between gap-4">
            <span className="min-w-0">
              <span className="block text-sm text-foreground">Organization admin</span>
              <span className="mt-0.5 block text-xs leading-4 text-muted-foreground">
                {canManage
                  ? "Admins manage members, usage limits, the company profile and branding."
                  : "Only organization admins can change roles."}
              </span>
            </span>
            <Switch
              aria-label={`Organization admin: ${name}`}
              checked={isAdmin}
              disabled={!canManage || busy}
              onCheckedChange={(checked) => setRole(checked)}
              className="mt-0.5"
            />
          </label>
        </SheetSection>

        <SheetSection
          title="Usage this month"
          action={
            canManage || isOperator ? (
              <Button size="xs" variant="outline" onClick={() => setLimitsOpen(true)}>
                <SlidersHorizontalIcon />
                Edit limits
              </Button>
            ) : null
          }
        >
          <MemberUsagePanel
            member={member}
            standardGbp={standardGbp}
            spend={spendFor(member)}
            isViewer={isViewer}
          />
        </SheetSection>

        {error ? (
          <p role="alert" className="text-sm text-destructive">
            {error}
          </p>
        ) : null}

        {canManage ? (
          <SheetSection title="Membership">
            <div className="flex items-center justify-between gap-4">
              <p className="text-xs leading-4 text-muted-foreground">
                {isViewer
                  ? "Leave this organization. You lose access straight away."
                  : `${name} loses access to this organization straight away.`}
              </p>
              <Button
                size="sm"
                variant="destructive-outline"
                disabled={busy}
                onClick={() => setConfirmRemove(true)}
              >
                <UserMinusIcon />
                {isViewer ? "Leave" : "Remove"}
              </Button>
            </div>
          </SheetSection>
        ) : null}
      </SheetPanel>

      <MemberAllowanceDialog
        open={limitsOpen}
        onOpenChange={setLimitsOpen}
        member={member}
        standardGbp={standardGbp}
        overageMultiplier={overageMultiplier}
        canManage={canManage}
        isOperator={isOperator}
        onToggleOnDemand={onToggleOnDemand}
        onSetAllowance={onSetAllowance}
      />

      <AlertDialog open={confirmRemove} onOpenChange={(open) => !busy && setConfirmRemove(open)}>
        <AlertDialogPopup className="max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {isViewer ? "Leave this organization?" : `Remove ${name}?`}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {isViewer
                ? "You lose access to its chats, library and settings until an admin invites you again."
                : `${name} loses access to the organization's chats, library and settings until they are invited again.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogClose render={<Button variant="ghost" disabled={busy} />}>
              Cancel
            </AlertDialogClose>
            <Button variant="destructive" disabled={busy} onClick={remove}>
              {busy ? "Removing…" : isViewer ? "Leave organization" : "Remove from organization"}
            </Button>
          </AlertDialogFooter>
        </AlertDialogPopup>
      </AlertDialog>
    </>
  );
}

function SheetSection({
  title,
  action,
  children,
}: {
  readonly title: string;
  readonly action?: ReactNode;
  readonly children: ReactNode;
}) {
  return (
    <section className="space-y-3 border-t border-border/60 pt-5 first:border-t-0 first:pt-0">
      <div className="flex min-h-6 items-center justify-between gap-3">
        <h3 className="text-xs font-medium tracking-wide text-muted-foreground uppercase">
          {title}
        </h3>
        {action}
      </div>
      {children}
    </section>
  );
}

function Detail({ label, value }: { readonly label: string; readonly value: string }) {
  return (
    <div className="min-w-0 space-y-0.5">
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className="truncate text-foreground" title={value}>
        {value}
      </dd>
    </div>
  );
}
